import type { Document, OrgScopedBaseUser } from "@models";
import type {
  TaskProgram,
  TrainingCompletionMethod,
  UnifiedTask,
} from "src/services/backend";

export type TrainingMatrixStatus =
  | "COMPLETED"
  | "PENDING"
  | "OVERDUE"
  | "AWAITING_QUIZ_PUBLICATION"
  | "NOT_ISSUED";

export type TrainingMatrixFunctionalGroup = {
  id: string;
  name: string;
  acronym: string;
};

export type TaskProgramUserMatrixAssignment = {
  taskStateId: string;
  programId: string;
  programName: string;
  functionalGroups: TrainingMatrixFunctionalGroup[];
  status: TrainingMatrixStatus;
  dueDate: string | null;
  assignedAt: string | null;
  completedAt: string | null;
  documentId: string;
  documentTitle: string;
  revision: number | null;
  completionMethod?: TrainingCompletionMethod;
};

export type TaskProgramUserMatrixRow = {
  trainee: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
  functionalGroups: TrainingMatrixFunctionalGroup[];
  status: TrainingMatrixStatus;
  nextDueDate: string | null;
  assignedAt: string | null;
  completedCount: number;
  totalCount: number;
  assignments: TaskProgramUserMatrixAssignment[];
};

type BuildTrainingUserMatrixRowsInput = {
  tasks: UnifiedTask[];
  programs: TaskProgram[];
  users: OrgScopedBaseUser[];
  documents: Record<string, Document>;
  evaluatedAt?: Date;
};

const uniqueFunctionalGroups = (
  groups: TrainingMatrixFunctionalGroup[]
): TrainingMatrixFunctionalGroup[] =>
  [...new Map(groups.map((group) => [group.id, group])).values()].sort((a, b) =>
    a.acronym.localeCompare(b.acronym)
  );

const compareDates = (a: string, b: string): number =>
  Date.parse(a) - Date.parse(b);

const compareOptionalDatesDescending = (
  a: string | null,
  b: string | null
): number => {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return compareDates(b, a);
};

export const formatTrainingUserName = (
  user: TaskProgramUserMatrixRow["trainee"]
): string =>
  [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
  user.email;

export const isIssuedTrainingMatrixStatus = (
  status: TrainingMatrixStatus
): boolean => status !== "NOT_ISSUED" && status !== "AWAITING_QUIZ_PUBLICATION";

const compareAssignments = (
  a: TaskProgramUserMatrixAssignment,
  b: TaskProgramUserMatrixAssignment
): number => {
  const aAwaitsQuiz = a.status === "AWAITING_QUIZ_PUBLICATION";
  const bAwaitsQuiz = b.status === "AWAITING_QUIZ_PUBLICATION";
  if (aAwaitsQuiz !== bAwaitsQuiz) return aAwaitsQuiz ? -1 : 1;
  if (aAwaitsQuiz && bAwaitsQuiz && a.revision !== b.revision) {
    if (a.revision == null) return 1;
    if (b.revision == null) return -1;
    return b.revision - a.revision;
  }
  return (
    compareOptionalDatesDescending(a.assignedAt, b.assignedAt) ||
    a.programName.localeCompare(b.programName)
  );
};

export const formatTrainingMatrixDateTime = (value: string | null): string => {
  if (!value) return "";
  return new Date(value).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const buildTrainingUserMatrixRows = ({
  tasks,
  programs,
  users,
  documents,
  evaluatedAt = new Date(),
}: BuildTrainingUserMatrixRowsInput): TaskProgramUserMatrixRow[] => {
  const programsById = new Map(
    programs.map((program) => [program.id, program])
  );
  const usersById = new Map(users.map((user) => [user.id, user]));
  const functionalGroupsByProgramId = new Map(
    programs.map((program) => [
      program.id,
      uniqueFunctionalGroups(
        program.assignments.flatMap((assignment) =>
          assignment.functionalGroup ? [assignment.functionalGroup] : []
        )
      ),
    ])
  );
  const issuedTaskProgramUsers = new Set<string>();
  const issuedTaskProgramRevisionUsers = new Set<string>();
  const processedProgramUsers = new Set<string>();
  const rowsByUser = new Map<
    string,
    Pick<TaskProgramUserMatrixRow, "trainee" | "functionalGroups"> & {
      assignments: TaskProgramUserMatrixAssignment[];
    }
  >();
  const getRow = (trainee: TaskProgramUserMatrixRow["trainee"]) => {
    let row = rowsByUser.get(trainee.id);
    if (!row) {
      row = {
        trainee,
        functionalGroups: uniqueFunctionalGroups(
          (usersById.get(trainee.id)?.organizationMemberships ?? []).flatMap(
            (membership) =>
              (membership.functionalGroupMemberships ?? []).map(
                ({ functionalGroup }) => functionalGroup
              )
          )
        ),
        assignments: [],
      };
      rowsByUser.set(trainee.id, row);
    }
    return row;
  };

  for (const task of tasks) {
    if (
      task.taskType !== "TRAINING" ||
      !task.assignee ||
      !task.sourceProgramId ||
      !task.documentId
    ) {
      continue;
    }

    const { assignee, documentId, sourceProgramId } = task;
    const program = programsById.get(sourceProgramId);
    const document = documents[documentId];
    issuedTaskProgramUsers.add(`${sourceProgramId}:${assignee.id}`);
    if (task.documentVersionIdToTrainOn) {
      issuedTaskProgramRevisionUsers.add(
        `${sourceProgramId}:${task.documentVersionIdToTrainOn}:${assignee.id}`
      );
    }
    getRow({
      id: assignee.id,
      email: assignee.email,
      firstName: assignee.firstName || null,
      lastName: assignee.lastName || null,
    }).assignments.push({
      taskStateId: task.id,
      programId: sourceProgramId,
      programName: task.sourceProgramName ?? program?.name ?? task.title,
      functionalGroups: functionalGroupsByProgramId.get(sourceProgramId) ?? [],
      status:
        task.status === "DONE"
          ? "COMPLETED"
          : task.dueDate && Date.parse(task.dueDate) < evaluatedAt.getTime()
            ? "OVERDUE"
            : "PENDING",
      dueDate: task.dueDate,
      assignedAt: task.createdAt,
      completedAt: task.trainingSignedAt ?? null,
      documentId,
      documentTitle:
        document?.title ?? program?.linkedDocument.title ?? task.title,
      revision:
        document?.versions.find(
          (version) => version.id === task.documentVersionIdToTrainOn
        )?.revision ?? null,
      completionMethod: task.trainingQuizId ? "QUIZ" : "ACKNOWLEDGEMENT",
    });
  }

  for (const program of programs) {
    for (const assignment of program.assignments) {
      for (const user of assignment.user
        ? [usersById.get(assignment.user.id) ?? assignment.user]
        : users.filter((user) =>
            user.organizationMemberships?.some((membership) =>
              membership.functionalGroupMemberships?.some(
                ({ functionalGroup }) =>
                  functionalGroup.id === assignment.functionalGroupId
              )
            )
          )) {
        const taskProgramUser = `${program.id}:${user.id}`;
        if (processedProgramUsers.has(taskProgramUser)) continue;
        processedProgramUsers.add(taskProgramUser);
        const row = getRow({
          id: user.id,
          email: user.email,
          firstName: user.firstName || null,
          lastName: user.lastName || null,
        });
        const document = documents[program.linkedDocumentId];
        const pendingQuizRevisions =
          program.completionMethod === "QUIZ"
            ? (program.quizzes ?? []).flatMap((quiz) => {
                if (
                  quiz.publishedAt ||
                  issuedTaskProgramRevisionUsers.has(
                    `${program.id}:${quiz.documentVersionId}:${user.id}`
                  )
                ) {
                  return [];
                }
                const version = document?.versions.find(
                  ({ id }) => id === quiz.documentVersionId
                );
                return version?.revision != null
                  ? [{ quiz, revision: version.revision }]
                  : [];
              })
            : [];

        row.assignments.push(
          ...pendingQuizRevisions.map(({ quiz, revision }) => ({
            taskStateId: `program:${program.id}:revision:${quiz.documentVersionId}:user:${user.id}`,
            programId: program.id,
            programName: program.name,
            functionalGroups: functionalGroupsByProgramId.get(program.id) ?? [],
            status: "AWAITING_QUIZ_PUBLICATION" as const,
            dueDate: null,
            assignedAt: null,
            completedAt: null,
            documentId: program.linkedDocumentId,
            documentTitle: document?.title ?? program.linkedDocument.title,
            revision,
            completionMethod: "QUIZ" as const,
          }))
        );

        if (
          issuedTaskProgramUsers.has(taskProgramUser) ||
          pendingQuizRevisions.length > 0
        ) {
          continue;
        }
        row.assignments.push({
          taskStateId: `program:${program.id}:user:${user.id}`,
          programId: program.id,
          programName: program.name,
          functionalGroups: functionalGroupsByProgramId.get(program.id) ?? [],
          status: "NOT_ISSUED",
          dueDate: null,
          assignedAt: null,
          completedAt: null,
          documentId: program.linkedDocumentId,
          documentTitle:
            documents[program.linkedDocumentId]?.title ??
            program.linkedDocument.title,
          revision: null,
          completionMethod: program.completionMethod ?? "ACKNOWLEDGEMENT",
        });
      }
    }
  }

  return [...rowsByUser.values()]
    .map<TaskProgramUserMatrixRow>(
      ({ trainee, functionalGroups, assignments }) => {
        assignments.sort(compareAssignments);
        const issuedAssignments = assignments.filter(({ status }) =>
          isIssuedTrainingMatrixStatus(status)
        );
        const hasPendingQuiz = assignments.some(
          ({ status }) => status === "AWAITING_QUIZ_PUBLICATION"
        );
        return {
          trainee,
          functionalGroups,
          status:
            issuedAssignments.length === 0
              ? hasPendingQuiz
                ? "AWAITING_QUIZ_PUBLICATION"
                : "NOT_ISSUED"
              : issuedAssignments.some(({ status }) => status === "OVERDUE")
                ? "OVERDUE"
                : issuedAssignments.some(({ status }) => status === "PENDING")
                  ? "PENDING"
                  : "COMPLETED",
          nextDueDate:
            issuedAssignments
              .flatMap(({ dueDate, status }) =>
                status !== "COMPLETED" && dueDate ? [dueDate] : []
              )
              .sort(compareDates)[0] ?? null,
          assignedAt:
            issuedAssignments[issuedAssignments.length - 1]?.assignedAt ?? null,
          completedCount: issuedAssignments.filter(
            ({ status }) => status === "COMPLETED"
          ).length,
          totalCount: issuedAssignments.length,
          assignments,
        };
      }
    )
    .sort((a, b) =>
      formatTrainingUserName(a.trainee).localeCompare(
        formatTrainingUserName(b.trainee),
        undefined,
        { sensitivity: "base" }
      )
    );
};
